// ── Expenses › Expense records — the register and the full-screen record window (batch 7 R3a) ──────
// One row per expense: a client's upload together with the bill made from it, or a bill with no upload.
// Its own file (the multi-tenant packages' convention, mt/index.ts) so the round's keys never edit the
// same lines as acctws.ts. ⛔ No `_plural` keys (dead in i18next 23); a number shown in a sentence is
// passed as {{n}} / {{no}}, never as `count`, so no plural lookup is ever triggered. ⛔ No empty value
// in any language: an empty Thai string renders a blank.
export const en: Record<string, string> = {
  aw_er_view_records: 'Expense records',
  aw_er_view_vat: 'VAT line items',
  aw_er_view_label: 'View',
  aw_er_no_portal_vat: 'This company has no invoicing-portal account, so it has no expense records yet. Its VAT line items are shown instead.',
  aw_er_search_ph: 'Search EXP no., supplier doc no., supplier or tax ID',
  aw_er_sb_search_ph: 'Search EXP no., supplier doc no., supplier, tax ID or file',
  aw_er_doc_all: 'All',
  aw_er_doc_draft: 'Draft',
  aw_er_doc_approved: 'Approved',
  aw_er_doc_posted: 'Posted',
  aw_er_doc_voided: 'Voided',
  aw_er_doc_filter: 'Document status',
  aw_er_pay_filter: 'Payment status',
  aw_er_pay_all: 'Any',
  aw_er_pay_unpaid: 'Unpaid',
  aw_er_pay_partial: 'Partially paid',
  aw_er_pay_paid: 'Paid',
  aw_er_pay_none: 'No bill',
  aw_er_pay_none_title: 'No payable is recorded for this record, so it has no payment status.',
  aw_er_from: 'From',
  aw_er_to: 'To',
  aw_er_clear_dates: 'Clear the dates',
  aw_er_col_no: 'Expense No.',
  aw_er_col_date: 'Date',
  aw_er_col_supplier: 'Supplier',
  aw_er_col_supplier_no: 'Supplier doc no.',
  aw_er_col_category: 'Category',
  aw_er_col_total: 'Total',
  aw_er_col_paid: 'Paid',
  aw_er_col_outstanding: 'Outstanding',
  aw_er_col_doc_status: 'Document status',
  aw_er_col_pay_status: 'Payment status',
  aw_er_col_actions: 'Actions',
  aw_er_sort_title: 'Sort by this column',
  aw_er_to_pay: 'To pay after WHT',
  aw_er_no_number: 'No EXP number: recorded before numbering began.',
  aw_er_category_multiple: 'Multiple ({{n}})',
  aw_er_category_none: 'No account chosen yet',
  aw_er_open: 'Open',
  aw_er_open_title: 'Open this expense record in full screen',
  aw_er_none: 'No expense records match.',
  aw_er_unlinked_input: '{{n}} input VAT line(s) of this client are not held by any expense record — entered straight into the VAT ledger, which is a way in this register does not replace.',
  aw_er_note: 'One row per expense: an upload together with the bill made from it, or a bill with no upload. Voided records are hidden unless you pick Voided. Paid and Outstanding are the bill’s own figures.',
  aw_er_window_title: 'Expense record',
  aw_er_source_bill: 'Figures from the bill',
  aw_er_source_line: 'Figures from the approved VAT line',
  aw_er_source_ocr: 'Figures as the scan was read (OCR)',
  aw_er_chip_withdrawn: 'Withdrawn by client',
  aw_er_chip_deleted: 'Deleted',
  aw_er_chip_void: 'Bill voided',
  aw_er_chip_cancelled: 'Cancelled by firm',
  aw_er_chip_returned: 'Returned to client',
  aw_er_chip_line_withdrawn: 'VAT line withdrawn',
  aw_er_chip_not_vat: 'Not VAT-registered',
  aw_er_chip_posted_line_withdrawn: 'VAT line withdrawn but entry {{no}} still posted',
  aw_er_chip_reversal_pending: 'Reversal {{no}} pending',
  aw_er_chip_posted_twice: 'Posted twice',
  aw_er_chip_entry_drafted: 'Entry {{no}} drafted',
  aw_er_chip_posted_as: 'Posted as {{no}}',
  aw_er_chip_shared_line: 'Same VAT line as {{refs}}',
  aw_er_chip_shared_line_title: 'Another record holds this same input VAT line. The two are not linked automatically — check which one is the supplier’s invoice before acting on either.',
  aw_er_chip_entry_title: 'Open this journal entry',
  aw_er_record_unnumbered: 'a record with no EXP number',
  aw_er_ref_scan: 'the uploaded scan {{no}}',
  aw_er_ref_bill: 'the bill {{no}}',
  aw_er_facts_title: 'The record',
  aw_er_compare_title: 'Where the sources differ',
  aw_er_compare_hint: 'Each source as it is stored — nothing here is recalculated. A marked row is a field on which two sources disagree.',
  aw_er_compare_field: 'Field',
  aw_er_compare_bill: 'Bill',
  aw_er_compare_line: 'VAT line',
  aw_er_compare_ocr: 'Scan (OCR)',
  aw_er_compare_differs: 'Differs',
  aw_er_f_supplier_no: 'Supplier doc no.',
  aw_er_f_date: 'Date',
  aw_er_f_supplier: 'Supplier',
  aw_er_f_supplier_tax_id: 'Tax ID',
  aw_er_f_total: 'Total',
  aw_er_f_vat: 'VAT',
  aw_er_scan_title: 'The uploaded scan',
  aw_er_download: 'Download',
  aw_er_download_title: 'Download the scan file to this computer',
  aw_er_upload_readonly: 'The scan is kept exactly as the client sent it, and the figures OCR read stay as evidence. Approving writes the figures you confirm into the VAT line.',
  aw_er_upload_withdrawn: 'The client withdrew this upload. It keeps its EXP number so the series has no unexplained gap.',
  aw_er_bill_title: 'Supplier bill',
  aw_er_no_bill: 'No bill has been recorded for this upload yet.',
  aw_er_bill_deleted: 'This bill was deleted. It is shown as it stood, with nothing left to act on.',
  aw_er_line_title: 'VAT line',
  aw_er_line_in: 'Input VAT line, ภ.พ.30 {{period}}',
  aw_er_line_withdrawn: 'This VAT line has been withdrawn from the return.',
  aw_er_void_locked: 'Posted twice — reverse the duplicate entry in Journal before voiding this bill.',
  aw_er_load_failed: 'This record could not be read',
  // ── Batch 7 R3b: the scan's own doors ──
  aw_er_act_approve: 'Approve',
  aw_er_act_reject: 'Return to client',
  aw_er_act_void: 'Void record',
  aw_er_act_to_draft: 'Return to draft',
  aw_er_act_pay: 'Pay',
  aw_er_approve_title: 'Approve into the VAT return',
  aw_er_approve_target: 'This invoice goes into the ภ.พ.30 for {{period}} — {{filing}}.',
  aw_er_filing_normal: 'the Normal return, not yet filed',
  aw_er_filing_additional: 'Additional Filing No. {{n}}',
  aw_er_approve_hint: 'The figures below are the scan as OCR read it. Correct anything that differs from the paper: your figures are what the VAT line records, and the OCR stays as evidence.',
  aw_er_approve_has_vat: 'The invoice shows VAT (untick for a purchase without VAT)',
  aw_er_f_branch: 'Branch',
  aw_er_f_base: 'Amount before VAT',
  aw_er_f_base0: 'Amount without VAT',
  aw_er_items_title: 'Items on the scan',
  aw_er_items_hint: 'Choose the account each item books to, from this client’s own chart. Leave one blank and the books decide as before.',
  aw_er_items_none: 'OCR read no item lines from this scan, so there is nothing to classify item by item.',
  aw_er_item_desc: 'Item',
  aw_er_item_qty: 'Qty',
  aw_er_item_price: 'Unit price',
  aw_er_item_amount: 'Amount',
  aw_er_item_code: 'Printed code',
  aw_er_item_account: 'Account',
  aw_er_item_account_none: 'No account chosen',
  aw_er_approve_btn: 'Approve',
  aw_er_reject_title: 'Return the scan to the client',
  aw_er_reject_hint: 'The client reads this reason in their portal and can send the invoice again.',
  aw_er_void_title: 'Void this expense record',
  aw_er_void_hint: 'Its VAT line comes out of the ภ.พ.30 and the bill made from it, if any, is voided too. The EXP number is kept, and the client sees “Cancelled by firm”.',
  aw_er_to_draft_title: 'Return this record to draft',
  aw_er_to_draft_hint: 'The approval is withdrawn: its VAT line comes out of the ภ.พ.30 and the scan is pending again, ready to be approved with corrected figures.',
  aw_er_pay_title: 'Pay this expense',
  aw_er_pay_hint: 'A bill is recorded from this upload with the figures below (as the scan was read — check them and the WHT), and the payment is recorded on it in the same step.',
  aw_er_pay_bill: 'The bill',
  aw_er_pay_payment: 'The payment',
  aw_er_pay_btn: 'Record bill and payment',
  aw_er_why_tax_module: 'Reviewing a scan writes the client’s VAT return, which needs the Tax module as well as the books.',
  aw_er_why_not_vat: 'This client is not VAT-registered, so its purchases do not go into a ภ.พ.30 input line.',
  aw_er_why_filed_approve: 'The ภ.พ.30 for {{period}} has been filed — approving would add this invoice to Additional Filing No. {{n}} of that month. Leave it pending, or return it to the client.',
  aw_er_why_outside: 'This company is outside Thailand, so its purchase bills are not booked into Thai input tax.',
  aw_er_why_bill_booked: 'The bill made from this scan ({{bill}}) is already booked into the VAT return.',
  aw_er_why_live_bill: 'A bill ({{bill}}) has been recorded from this scan — void or delete it first.',
  aw_er_why_payments: 'The bill made from this scan ({{bill}}) has payments — take them off first.',
  aw_er_why_posted: 'Posted in the books as {{no}} — reverse it in Journal and post the reversal first.',
  aw_er_why_posted_closed: 'Posted in the books as {{no}}, in the accounting period {{from}} – {{to}}, which is closed — it stays in the books; enter only the difference in the current period.',   // batch 11 W4 P9 (LB-41)
  aw_er_why_asset: 'Fixed asset {{asset}} was registered on this expense — remove it from the asset register first.',
  aw_er_create_asset: 'Register as fixed asset',
  aw_er_why_filed_line: 'Its VAT line is in a ภ.พ.30 submission that has been filed — a filed return is corrected by an Additional Filing in the Tax module.',
  aw_er_why_notes_booked: 'A supplier note booked into the VAT return adjusts this record’s bill — unbook the note first (Purchases › Debit & credit notes).',   // batch 11 R8b: a booked supplier note holds the bill's line (W2-36)
  aw_er_why_settlement_posted: 'A payment of this record’s bill is settled in the books as {{no}} — reverse that settlement first (the bill’s payment history opens it).',   // batch 11 W3 R6: settlement_posted (W3-15)
  aw_er_why_settlement_posted_closed: 'A payment of this record’s bill is settled in the books as {{no}}, posted in the period {{from}} – {{to}}, which is closed — it is not reversed, so this record stays in the VAT return as it is. Enter only a difference as a manual voucher in the current period.',   // W3 ship review: a closed period's settlement is never reversed
  aw_er_why_settlement_posted_rev: 'A payment of this record’s bill is settled in the books as {{no}}, and its reversal {{rv}} is still a draft — post {{rv}} first (the bill’s payment history opens it).',
  aw_er_cancelled_by: 'Cancelled by {{by}} on {{when}}: {{reason}}',
  aw_er_detached_note: 'Its VAT line was kept: another expense record still holds the same line, so only this record let go of it.',
};

export const zh: Record<string, string> = {
  aw_er_view_records: '费用记录',
  aw_er_view_vat: '进项税行',
  aw_er_view_label: '视图',
  aw_er_no_portal_vat: '这家公司还没有开票门户账号，所以还没有费用记录。下面显示的是它的进项税行。',
  aw_er_search_ph: '搜索 EXP 号、供应商单号、供应商或税号',
  aw_er_sb_search_ph: '搜索 EXP 号、供应商单号、供应商、税号或文件名',
  aw_er_doc_all: '全部',
  aw_er_doc_draft: '草稿',
  aw_er_doc_approved: '已审核',
  aw_er_doc_posted: '已过账',
  aw_er_doc_voided: '已作废',
  aw_er_doc_filter: '单据状态',
  aw_er_pay_filter: '付款状态',
  aw_er_pay_all: '不限',
  aw_er_pay_unpaid: '未付',
  aw_er_pay_partial: '部分付款',
  aw_er_pay_paid: '已付',
  aw_er_pay_none: '无账单',
  aw_er_pay_none_title: '这条记录还没有登记应付账单，所以没有付款状态。',
  aw_er_from: '起',
  aw_er_to: '止',
  aw_er_clear_dates: '清除日期',
  aw_er_col_no: '费用编号',
  aw_er_col_date: '日期',
  aw_er_col_supplier: '供应商',
  aw_er_col_supplier_no: '供应商单号',
  aw_er_col_category: '分类',
  aw_er_col_total: '合计',
  aw_er_col_paid: '已付',
  aw_er_col_outstanding: '未付',
  aw_er_col_doc_status: '单据状态',
  aw_er_col_pay_status: '付款状态',
  aw_er_col_actions: '操作',
  aw_er_sort_title: '按此列排序',
  aw_er_to_pay: '扣 WHT 后应付',
  aw_er_no_number: '没有 EXP 号：这条记录早于编号上线。',
  aw_er_category_multiple: '多个（{{n}}）',
  aw_er_category_none: '尚未选择科目',
  aw_er_open: '打开',
  aw_er_open_title: '全屏打开这条费用记录',
  aw_er_none: '没有符合条件的费用记录。',
  aw_er_unlinked_input: '本客户有 {{n}} 条进项税明细不属于任何费用记录 —— 它们是直接录在增值税账簿里的,这条路本登记簿并不取代。',
  aw_er_note: '一行就是一笔费用：一张上传件连同由它生成的账单，或者一张没有上传件的账单。已作废的记录默认不显示，点「已作废」才显示。已付和未付读的是账单自己的数字。',
  aw_er_window_title: '费用记录',
  aw_er_source_bill: '数字取自账单',
  aw_er_source_line: '数字取自已审核的进项税行',
  aw_er_source_ocr: '数字为扫描识别（OCR）结果',
  aw_er_chip_withdrawn: '客户已撤回',
  aw_er_chip_deleted: '已删除',
  aw_er_chip_void: '账单已作废',
  aw_er_chip_cancelled: '事务所已作废',
  aw_er_chip_returned: '已退回客户',
  aw_er_chip_line_withdrawn: '进项税行已撤回',
  aw_er_chip_not_vat: '未登记增值税',
  aw_er_chip_posted_line_withdrawn: '进项税行已撤回，但分录 {{no}} 仍在账上',
  aw_er_chip_reversal_pending: '冲销 {{no}} 尚未过账',
  aw_er_chip_posted_twice: '重复过账',
  aw_er_chip_entry_drafted: '已生成草稿分录 {{no}}',
  aw_er_chip_posted_as: '已过账为 {{no}}',
  aw_er_chip_shared_line: '与 {{refs}} 共用同一条进项税行',
  aw_er_chip_shared_line_title: '另一条记录持有同一条进项税行。两者不会自动关联——处理任何一条之前，请先核对哪一条才是供应商的发票。',
  aw_er_chip_entry_title: '打开这张分录',
  aw_er_record_unnumbered: '一条没有 EXP 号的记录',
  aw_er_ref_scan: '上传的扫描件 {{no}}',
  aw_er_ref_bill: '账单 {{no}}',
  aw_er_facts_title: '记录',
  aw_er_compare_title: '各来源的差异',
  aw_er_compare_hint: '各来源按存储原样显示，这里不做任何重算。标出的行表示两个来源在该字段上不一致。',
  aw_er_compare_field: '字段',
  aw_er_compare_bill: '账单',
  aw_er_compare_line: '进项税行',
  aw_er_compare_ocr: '扫描件（OCR）',
  aw_er_compare_differs: '不一致',
  aw_er_f_supplier_no: '供应商单号',
  aw_er_f_date: '日期',
  aw_er_f_supplier: '供应商',
  aw_er_f_supplier_tax_id: '税号',
  aw_er_f_total: '合计',
  aw_er_f_vat: '增值税',
  aw_er_scan_title: '上传的扫描件',
  aw_er_download: '下载',
  aw_er_download_title: '把扫描件文件下载到本机',
  aw_er_upload_readonly: '扫描件按客户发来的原样保留，OCR 读出的数字作为证据保存。审核时写入税务行的是你确认的数字。',
  aw_er_upload_withdrawn: '客户已撤回这张上传件。它保留 EXP 号，序列里因此不会出现无法解释的空号。',
  aw_er_bill_title: '供应商账单',
  aw_er_no_bill: '这张上传件还没有登记账单。',
  aw_er_bill_deleted: '这张账单已删除，按删除时的样子显示，已没有可操作的内容。',
  aw_er_line_title: '进项税行',
  aw_er_line_in: '进项税行，ภ.พ.30 {{period}}',
  aw_er_line_withdrawn: '这条进项税行已从申报中撤回。',
  aw_er_void_locked: '重复过账——请先在 Journal 冲销多出的那张分录，再作废这张账单。',
  aw_er_load_failed: '无法读取这条记录',
  // ── Batch 7 R3b ──
  aw_er_act_approve: '审核通过',
  aw_er_act_reject: '退回客户',
  aw_er_act_void: '作废记录',
  aw_er_act_to_draft: '退回草稿',
  aw_er_act_pay: '付款',
  aw_er_approve_title: '审核并计入增值税申报',
  aw_er_approve_target: '这张发票将计入 {{period}} 的 ภ.พ.30 —— {{filing}}。',
  aw_er_filing_normal: '正常申报，尚未申报',
  aw_er_filing_additional: '第 {{n}} 次增补申报',
  aw_er_approve_hint: '下面的数字是 OCR 从扫描件读出的。与纸面不符的请改正：税务行记录的是你确认的数字，OCR 结果作为证据保留。',
  aw_er_approve_has_vat: '发票上有增值税（无增值税的采购请取消勾选）',
  aw_er_f_branch: '分支',
  aw_er_f_base: '税前金额',
  aw_er_f_base0: '不含增值税的金额',
  aw_er_items_title: '扫描件上的项目',
  aw_er_items_hint: '从本客户自己的科目表里为每个项目选择记账科目。留空的项目照旧由账簿决定。',
  aw_er_items_none: 'OCR 没有从这张扫描件读出项目明细，所以没有逐项归类的内容。',
  aw_er_item_desc: '项目',
  aw_er_item_qty: '数量',
  aw_er_item_price: '单价',
  aw_er_item_amount: '金额',
  aw_er_item_code: '印刷编码',
  aw_er_item_account: '科目',
  aw_er_item_account_none: '未选科目',
  aw_er_approve_btn: '审核通过',
  aw_er_reject_title: '把扫描件退回客户',
  aw_er_reject_hint: '客户会在门户里看到这个原因，并可以重新发送发票。',
  aw_er_void_title: '作废这条费用记录',
  aw_er_void_hint: '它的税务行会从 ภ.พ.30 中撤出，由它生成的账单（如有）也一并作废。EXP 号保留，客户会看到「已被事务所作废」。',
  aw_er_to_draft_title: '把这条记录退回草稿',
  aw_er_to_draft_hint: '撤销审核：它的税务行从 ภ.พ.30 中撤出，扫描件回到待审，可以改正数字后重新审核。',
  aw_er_pay_title: '为这笔费用付款',
  aw_er_pay_hint: '用下面的数字（扫描件读出的，请核对数字和预扣税）从这张上传件生成一张账单，并在同一步里记录付款。',
  aw_er_pay_bill: '账单',
  aw_er_pay_payment: '付款',
  aw_er_pay_btn: '记录账单和付款',
  aw_er_why_tax_module: '审核扫描件会写入客户的增值税申报，需要税务模块和账簿权限。',
  aw_er_why_not_vat: '这个客户没有登记增值税，它的采购不进入 ภ.พ.30 进项税行。',
  aw_er_why_filed_approve: '{{period}} 的 ภ.พ.30 已经申报——审核会把这张发票加进该月第 {{n}} 次增补申报。请保持待审，或退回客户。',
  aw_er_why_outside: '这家公司不在泰国，它的采购账单不计入泰国进项税。',
  aw_er_why_bill_booked: '由这张扫描件生成的账单（{{bill}}）已经计入增值税申报。',
  aw_er_why_live_bill: '已经由这张扫描件记录了账单（{{bill}}）——请先作废或删除它。',
  aw_er_why_payments: '由这张扫描件生成的账单（{{bill}}）有付款——请先撤销付款。',
  aw_er_why_posted: '已在账簿中过账为 {{no}}——请先在 Journal 里冲销并过账冲销分录。',
  aw_er_why_posted_closed: '已在账簿中过账为 {{no}}，所在会计期间 {{from}} – {{to}} 已结账——它会留在账上；请只在本期录入差额。',
  aw_er_why_asset: '已有固定资产 {{asset}} 登记在这笔费用上——请先把它从资产登记簿中移除。',
  aw_er_create_asset: '登记为固定资产',
  aw_er_why_filed_line: '它的税务行所在的 ภ.พ.30 申报已经申报——已申报的申报表要在税务模块里用增补申报更正。',
  aw_er_why_notes_booked: '有一张已计入 VAT 申报的供应商单据在调整这条记录的账单——请先把那张单据撤出申报（采购 › 采购增额与折让）。',
  aw_er_why_settlement_posted: '这条记录的账单有一笔付款已在账上结算为 {{no}}——请先冲销该结算分录（账单的付款记录中可打开它）。',
  aw_er_why_settlement_posted_closed: '这条记录的账单有一笔付款已在账上结算为 {{no}}，该分录过账在已结账的期间 {{from}} – {{to}}——它不会被冲销，所以这条记录按现状留在增值税申报中。差额请只在本期以手工凭证录入。',
  aw_er_why_settlement_posted_rev: '这条记录的账单有一笔付款已在账上结算为 {{no}}，其冲销分录 {{rv}} 仍是草稿——请先过账 {{rv}}（账单的付款记录中可打开它）。',
  aw_er_cancelled_by: '{{by}} 于 {{when}} 作废：{{reason}}',
  aw_er_detached_note: '税务行保留了：另一条费用记录仍持有同一条税务行，所以只解除了本记录与它的关联。',
};

export const th: Record<string, string> = {
  aw_er_view_records: 'รายการค่าใช้จ่าย',
  aw_er_view_vat: 'รายการภาษีซื้อ',
  aw_er_view_label: 'มุมมอง',
  aw_er_no_portal_vat: 'บริษัทนี้ยังไม่มีบัญชีพอร์ทัลออกเอกสาร จึงยังไม่มีรายการค่าใช้จ่าย ด้านล่างแสดงรายการภาษีซื้อของบริษัทแทน',
  aw_er_search_ph: 'ค้นหาเลข EXP เลขที่เอกสารผู้ขาย ผู้ขาย หรือเลขประจำตัวผู้เสียภาษี',
  aw_er_sb_search_ph: 'ค้นหาเลข EXP เลขที่เอกสารผู้ขาย ผู้ขาย เลขประจำตัวผู้เสียภาษี หรือชื่อไฟล์',
  aw_er_doc_all: 'ทั้งหมด',
  aw_er_doc_draft: 'ฉบับร่าง',
  aw_er_doc_approved: 'อนุมัติแล้ว',
  aw_er_doc_posted: 'ลงบัญชีแล้ว',
  aw_er_doc_voided: 'ยกเลิกแล้ว',
  aw_er_doc_filter: 'สถานะเอกสาร',
  aw_er_pay_filter: 'สถานะการชำระ',
  aw_er_pay_all: 'ทุกสถานะ',
  aw_er_pay_unpaid: 'ยังไม่ชำระ',
  aw_er_pay_partial: 'ชำระบางส่วน',
  aw_er_pay_paid: 'ชำระแล้ว',
  aw_er_pay_none: 'ไม่มีบิล',
  aw_er_pay_none_title: 'รายการนี้ยังไม่มีการบันทึกเจ้าหนี้ จึงไม่มีสถานะการชำระ',
  aw_er_from: 'จาก',
  aw_er_to: 'ถึง',
  aw_er_clear_dates: 'ล้างวันที่',
  aw_er_col_no: 'เลขที่ค่าใช้จ่าย',
  aw_er_col_date: 'วันที่',
  aw_er_col_supplier: 'ผู้ขาย',
  aw_er_col_supplier_no: 'เลขที่เอกสารผู้ขาย',
  aw_er_col_category: 'หมวดบัญชี',
  aw_er_col_total: 'ยอดรวม',
  aw_er_col_paid: 'ชำระแล้ว',
  aw_er_col_outstanding: 'คงค้าง',
  aw_er_col_doc_status: 'สถานะเอกสาร',
  aw_er_col_pay_status: 'สถานะการชำระ',
  aw_er_col_actions: 'การทำงาน',
  aw_er_sort_title: 'เรียงตามคอลัมน์นี้',
  aw_er_to_pay: 'ยอดจ่ายหลังหัก ณ ที่จ่าย',
  aw_er_no_number: 'ไม่มีเลข EXP: บันทึกไว้ก่อนเริ่มใช้การรันเลข',
  aw_er_category_multiple: 'หลายบัญชี ({{n}})',
  aw_er_category_none: 'ยังไม่ได้เลือกบัญชี',
  aw_er_open: 'เปิด',
  aw_er_open_title: 'เปิดรายการค่าใช้จ่ายนี้แบบเต็มจอ',
  aw_er_none: 'ไม่มีรายการค่าใช้จ่ายที่ตรงกับเงื่อนไข',
  aw_er_unlinked_input: 'ลูกค้ารายนี้มีรายการภาษีซื้อ {{n}} รายการที่ไม่ได้อยู่ในบันทึกค่าใช้จ่ายใด — บันทึกตรงในสมุดภาษีมูลค่าเพิ่ม ซึ่งทะเบียนนี้ไม่ได้แทนที่',
  aw_er_note: 'หนึ่งแถวคือค่าใช้จ่ายหนึ่งรายการ: ไฟล์ที่อัปโหลดพร้อมบิลที่สร้างจากไฟล์นั้น หรือบิลที่ไม่มีไฟล์อัปโหลด รายการที่ยกเลิกแล้วจะซ่อนไว้จนกว่าจะเลือก “ยกเลิกแล้ว” ยอดชำระแล้วและคงค้างเป็นตัวเลขของบิลเอง',
  aw_er_window_title: 'รายการค่าใช้จ่าย',
  aw_er_source_bill: 'ตัวเลขจากบิล',
  aw_er_source_line: 'ตัวเลขจากรายการภาษีซื้อที่อนุมัติแล้ว',
  aw_er_source_ocr: 'ตัวเลขตามที่อ่านจากไฟล์สแกน (OCR)',
  aw_er_chip_withdrawn: 'ลูกค้าถอนคืนแล้ว',
  aw_er_chip_deleted: 'ลบแล้ว',
  aw_er_chip_void: 'บิลถูกยกเลิก',
  aw_er_chip_cancelled: 'สำนักงานยกเลิกแล้ว',
  aw_er_chip_returned: 'ส่งคืนลูกค้าแล้ว',
  aw_er_chip_line_withdrawn: 'ถอนรายการภาษีซื้อแล้ว',
  aw_er_chip_not_vat: 'ไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม',
  aw_er_chip_posted_line_withdrawn: 'ถอนรายการภาษีซื้อแล้ว แต่รายการบัญชี {{no}} ยังลงบัญชีอยู่',
  aw_er_chip_reversal_pending: 'รายการกลับบัญชี {{no}} ยังไม่ลงบัญชี',
  aw_er_chip_posted_twice: 'ลงบัญชีซ้ำ',
  aw_er_chip_entry_drafted: 'มีร่างรายการบัญชี {{no}}',
  aw_er_chip_posted_as: 'ลงบัญชีเป็น {{no}}',
  aw_er_chip_shared_line: 'ใช้รายการภาษีซื้อเดียวกับ {{refs}}',
  aw_er_chip_shared_line_title: 'มีอีกรายการหนึ่งถือรายการภาษีซื้อเดียวกันนี้ ระบบไม่เชื่อมสองรายการนี้ให้อัตโนมัติ กรุณาตรวจว่ารายการใดคือใบกำกับภาษีของผู้ขายก่อนดำเนินการกับรายการใด',
  aw_er_chip_entry_title: 'เปิดรายการบัญชีนี้',
  aw_er_record_unnumbered: 'รายการที่ไม่มีเลข EXP',
  aw_er_ref_scan: 'ไฟล์สแกนที่อัปโหลด {{no}}',
  aw_er_ref_bill: 'บิล {{no}}',
  aw_er_facts_title: 'รายการ',
  aw_er_compare_title: 'จุดที่แหล่งข้อมูลไม่ตรงกัน',
  aw_er_compare_hint: 'แต่ละแหล่งแสดงตามที่บันทึกไว้ ไม่มีการคำนวณใหม่ แถวที่มีเครื่องหมายคือช่องที่สองแหล่งไม่ตรงกัน',
  aw_er_compare_field: 'ช่องข้อมูล',
  aw_er_compare_bill: 'บิล',
  aw_er_compare_line: 'รายการภาษีซื้อ',
  aw_er_compare_ocr: 'ไฟล์สแกน (OCR)',
  aw_er_compare_differs: 'ไม่ตรงกัน',
  aw_er_f_supplier_no: 'เลขที่เอกสารผู้ขาย',
  aw_er_f_date: 'วันที่',
  aw_er_f_supplier: 'ผู้ขาย',
  aw_er_f_supplier_tax_id: 'เลขประจำตัวผู้เสียภาษี',
  aw_er_f_total: 'ยอดรวม',
  aw_er_f_vat: 'ภาษีมูลค่าเพิ่ม',
  aw_er_scan_title: 'ไฟล์สแกนที่อัปโหลด',
  aw_er_download: 'ดาวน์โหลด',
  aw_er_download_title: 'ดาวน์โหลดไฟล์สแกนลงเครื่องนี้',
  aw_er_upload_readonly: 'ไฟล์สแกนเก็บไว้ตามที่ลูกค้าส่งมา และตัวเลขที่ OCR อ่านได้เก็บไว้เป็นหลักฐาน เมื่ออนุมัติ ระบบจะบันทึกตัวเลขที่ท่านยืนยันลงในรายการภาษีซื้อ',
  aw_er_upload_withdrawn: 'ลูกค้าถอนไฟล์อัปโหลดนี้แล้ว รายการยังคงเลข EXP ไว้ เพื่อไม่ให้มีเลขขาดหายโดยไม่มีคำอธิบาย',
  aw_er_bill_title: 'บิลผู้ขาย',
  aw_er_no_bill: 'ยังไม่มีการบันทึกบิลสำหรับไฟล์อัปโหลดนี้',
  aw_er_bill_deleted: 'บิลนี้ถูกลบแล้ว แสดงตามสภาพก่อนลบ และไม่มีสิ่งใดให้ดำเนินการต่อ',
  aw_er_line_title: 'รายการภาษีซื้อ',
  aw_er_line_in: 'รายการภาษีซื้อ ภ.พ.30 {{period}}',
  aw_er_line_withdrawn: 'รายการภาษีซื้อนี้ถูกถอนออกจากแบบแสดงรายการแล้ว',
  aw_er_void_locked: 'ลงบัญชีซ้ำ — กรุณากลับรายการบัญชีที่ซ้ำใน Journal ก่อนยกเลิกบิลนี้',
  aw_er_load_failed: 'อ่านรายการนี้ไม่ได้',
  // ── Batch 7 R3b ──
  aw_er_act_approve: 'อนุมัติ',
  aw_er_act_reject: 'ส่งคืนลูกค้า',
  aw_er_act_void: 'ยกเลิกรายการ',
  aw_er_act_to_draft: 'กลับเป็นฉบับร่าง',
  aw_er_act_pay: 'ชำระเงิน',
  aw_er_approve_title: 'อนุมัติเข้าแบบ ภ.พ.30',
  aw_er_approve_target: 'ใบกำกับนี้จะเข้าแบบ ภ.พ.30 เดือน {{period}} — {{filing}}',
  aw_er_filing_normal: 'แบบปกติ ยังไม่ได้ยื่น',
  aw_er_filing_additional: 'ยื่นเพิ่มเติมครั้งที่ {{n}}',
  aw_er_approve_hint: 'ตัวเลขด้านล่างคือสิ่งที่ OCR อ่านจากไฟล์สแกน โปรดแก้ไขส่วนที่ไม่ตรงกับเอกสารจริง รายการภาษีจะบันทึกตัวเลขที่ท่านยืนยัน ส่วนผลของ OCR เก็บไว้เป็นหลักฐาน',
  aw_er_approve_has_vat: 'ใบกำกับมีภาษีมูลค่าเพิ่ม (เอาเครื่องหมายออกหากเป็นการซื้อที่ไม่มีภาษี)',
  aw_er_f_branch: 'สาขา',
  aw_er_f_base: 'มูลค่าก่อนภาษี',
  aw_er_f_base0: 'มูลค่าที่ไม่มีภาษี',
  aw_er_items_title: 'รายการในไฟล์สแกน',
  aw_er_items_hint: 'เลือกบัญชีที่แต่ละรายการจะบันทึก จากผังบัญชีของลูกค้ารายนี้เอง รายการที่เว้นว่างไว้จะบันทึกแบบเดิม',
  aw_er_items_none: 'OCR ไม่ได้อ่านรายการสินค้าจากไฟล์สแกนนี้ จึงไม่มีรายการให้จัดหมวดทีละรายการ',
  aw_er_item_desc: 'รายการ',
  aw_er_item_qty: 'จำนวน',
  aw_er_item_price: 'ราคาต่อหน่วย',
  aw_er_item_amount: 'จำนวนเงิน',
  aw_er_item_code: 'รหัสที่พิมพ์บนเอกสาร',
  aw_er_item_account: 'บัญชี',
  aw_er_item_account_none: 'ยังไม่ได้เลือกบัญชี',
  aw_er_approve_btn: 'อนุมัติ',
  aw_er_reject_title: 'ส่งไฟล์สแกนคืนลูกค้า',
  aw_er_reject_hint: 'ลูกค้าจะเห็นเหตุผลนี้ในพอร์ทัล และส่งใบกำกับมาใหม่ได้',
  aw_er_void_title: 'ยกเลิกรายการค่าใช้จ่ายนี้',
  aw_er_void_hint: 'รายการภาษีจะถูกนำออกจากแบบ ภ.พ.30 และบิลที่สร้างจากไฟล์นี้ (ถ้ามี) จะถูกยกเลิกด้วย เลข EXP ยังคงเดิม และลูกค้าจะเห็นว่า “ยกเลิกโดยสำนักงาน”',
  aw_er_to_draft_title: 'ส่งรายการนี้กลับเป็นฉบับร่าง',
  aw_er_to_draft_hint: 'ถอนการอนุมัติ: รายการภาษีจะถูกนำออกจากแบบ ภ.พ.30 และไฟล์สแกนกลับไปรออนุมัติ เพื่อแก้ตัวเลขแล้วอนุมัติใหม่',
  aw_er_pay_title: 'ชำระค่าใช้จ่ายนี้',
  aw_er_pay_hint: 'ระบบจะบันทึกบิลจากไฟล์อัปโหลดนี้ด้วยตัวเลขด้านล่าง (ตามที่อ่านจากไฟล์สแกน โปรดตรวจตัวเลขและภาษีหัก ณ ที่จ่าย) และบันทึกการชำระในขั้นตอนเดียวกัน',
  aw_er_pay_bill: 'บิล',
  aw_er_pay_payment: 'การชำระเงิน',
  aw_er_pay_btn: 'บันทึกบิลและการชำระ',
  aw_er_why_tax_module: 'การตรวจไฟล์สแกนจะบันทึกลงแบบภาษีมูลค่าเพิ่มของลูกค้า จึงต้องมีสิทธิ์โมดูลภาษีด้วย นอกเหนือจากสิทธิ์สมุดบัญชี',
  aw_er_why_not_vat: 'ลูกค้ารายนี้ไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม การซื้อจึงไม่เข้ารายการภาษีซื้อในแบบ ภ.พ.30',
  aw_er_why_filed_approve: 'แบบ ภ.พ.30 เดือน {{period}} ยื่นแล้ว การอนุมัติจะเพิ่มใบกำกับนี้เข้าการยื่นเพิ่มเติมครั้งที่ {{n}} ของเดือนนั้น โปรดคงไว้ที่รออนุมัติ หรือส่งคืนลูกค้า',
  aw_er_why_outside: 'บริษัทนี้อยู่นอกประเทศไทย บิลซื้อจึงไม่บันทึกเป็นภาษีซื้อของไทย',
  aw_er_why_bill_booked: 'บิลที่สร้างจากไฟล์สแกนนี้ ({{bill}}) บันทึกเข้าแบบภาษีมูลค่าเพิ่มแล้ว',
  aw_er_why_live_bill: 'มีการบันทึกบิล ({{bill}}) จากไฟล์สแกนนี้แล้ว โปรดยกเลิกหรือลบบิลนั้นก่อน',
  aw_er_why_payments: 'บิลที่สร้างจากไฟล์สแกนนี้ ({{bill}}) มีการชำระเงินแล้ว โปรดลบรายการชำระก่อน',
  aw_er_why_posted: 'ลงบัญชีแล้วเป็น {{no}} โปรดกลับรายการใน Journal และผ่านรายการกลับก่อน',
  aw_er_why_posted_closed: 'ลงบัญชีแล้วเป็น {{no}} ในงวดบัญชี {{from}} – {{to}} ซึ่งปิดแล้ว — รายการนี้ยังคงอยู่ในบัญชี โปรดบันทึกเฉพาะส่วนต่างในงวดปัจจุบัน',
  aw_er_why_asset: 'มีสินทรัพย์ถาวร {{asset}} ลงทะเบียนจากค่าใช้จ่ายนี้แล้ว โปรดนำออกจากทะเบียนสินทรัพย์ก่อน',
  aw_er_create_asset: 'ลงทะเบียนเป็นสินทรัพย์ถาวร',
  aw_er_why_filed_line: 'รายการภาษีอยู่ในแบบ ภ.พ.30 ที่ยื่นแล้ว แบบที่ยื่นแล้วต้องแก้ไขด้วยการยื่นเพิ่มเติมในโมดูลภาษี',
  aw_er_why_notes_booked: 'มีใบลด/เพิ่มหนี้ของผู้ขายที่ลงในแบบยื่น VAT ปรับปรุงบิลของรายการนี้อยู่ — นำใบนั้นออกก่อน (การซื้อ › ใบเพิ่มหนี้และใบลดหนี้ (ซื้อ))',
  aw_er_why_settlement_posted: 'การชำระของบิลในรายการนี้ลงบัญชีแล้วเป็น {{no}} — โปรดกลับรายการบันทึกชำระนั้นก่อน (เปิดได้จากประวัติการชำระของบิล)',
  aw_er_why_settlement_posted_closed: 'การชำระของบิลในรายการนี้ลงบัญชีแล้วเป็น {{no}} ซึ่งผ่านรายการในงวด {{from}} – {{to}} ที่ปิดแล้ว — รายการนี้จะไม่ถูกกลับรายการ รายการนี้จึงคงอยู่ในแบบยื่น VAT ตามเดิม หากมีส่วนต่างให้บันทึกเฉพาะส่วนต่างเป็นใบสำคัญด้วยมือในงวดปัจจุบัน',
  aw_er_why_settlement_posted_rev: 'การชำระของบิลในรายการนี้ลงบัญชีแล้วเป็น {{no}} และรายการกลับ {{rv}} ยังเป็นร่างอยู่ — โปรดผ่านรายการ {{rv}} ก่อน (เปิดได้จากประวัติการชำระของบิล)',
  aw_er_cancelled_by: 'ยกเลิกโดย {{by}} เมื่อ {{when}}: {{reason}}',
  aw_er_detached_note: 'รายการภาษียังคงอยู่ เพราะรายการค่าใช้จ่ายอีกรายการยังถือรายการภาษีเดียวกัน จึงยกเลิกการเชื่อมโยงเฉพาะรายการนี้',
};
